<script setup lang="ts">
import { computed } from 'vue'

import { DEFAULT_TIME_ZONE, formatDate, isBusinessDate, toIsoString, type DatePreset } from '../lib/format'

/**
 * Render a date or an instant.
 *
 * The `timeZone` prop is not optional decoration — CLAUDE.md §5 puts every
 * tenant on an IANA timezone (default `Indian/Maldives`) and requires business
 * dates to be interpreted in it. Two rules follow, and this component enforces
 * both:
 *
 *  - a bare `YYYY-MM-DD` is a calendar fact and is rendered verbatim, never
 *    shifted, so an invoice dated the 31st never displays as the 30th;
 *  - a `timestamptz` instant is converted into the tenant timezone (or the
 *    user's profile override), never into the browser's.
 *
 * Renders `<time datetime>` so the machine value survives the display format.
 */

const props = withDefaults(
  defineProps<{
    value: string | Date | null | undefined
    /** IANA identifier from the tenant registry, or the user's override. */
    timeZone?: string
    preset?: DatePreset
    locale?: string
    placeholder?: string
    /** Append the zone abbreviation — worth it on audit and posting times. */
    showZone?: boolean
  }>(),
  { timeZone: DEFAULT_TIME_ZONE, preset: 'date', locale: 'en', placeholder: '—' },
)

const present = computed(
  () => props.value !== null && props.value !== undefined && props.value !== '',
)

const calendarOnly = computed(() => typeof props.value === 'string' && isBusinessDate(props.value))

const text = computed(() =>
  present.value
    ? formatDate(props.value, { locale: props.locale, timeZone: props.timeZone, preset: props.preset })
    : props.placeholder,
)

const machine = computed(() => toIsoString(props.value))

const zone = computed(() => {
  if (!props.showZone || calendarOnly.value || !present.value) return ''
  try {
    const parts = new Intl.DateTimeFormat(props.locale, {
      timeZone: props.timeZone,
      timeZoneName: 'short',
    }).formatToParts(props.value instanceof Date ? props.value : new Date(String(props.value)))
    return parts.find((part) => part.type === 'timeZoneName')?.value ?? ''
  } catch {
    return ''
  }
})
</script>

<template>
  <time v-if="present" class="e-date" :datetime="machine">
    {{ text }}<span v-if="zone !== ''" class="e-date__zone">{{ zone }}</span>
  </time>
  <span v-else class="e-date e-date--empty">{{ placeholder }}</span>
</template>

<style>
.e-date {
  font-variant-numeric: tabular-nums;
  font-size: var(--erpx-text-sm);
  white-space: nowrap;
}

.e-date--empty {
  color: var(--erpx-color-text-muted);
}

.e-date__zone {
  margin-inline-start: var(--erpx-space-1);
  color: var(--erpx-color-text-muted);
  font-size: var(--erpx-text-2xs);
}
</style>
